import React, { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { getRoles, createRole, updateRole, deleteRole, getPermissions } from '../../services/api';
import Toggle from '../../components/Toggle';
import { useConfirm } from '../../components/ConfirmDialog';

const CATEGORY_LABELS = {
  appointments: '📅 Agendamentos',
  users:        '👥 Usuários',
  billing:      '💰 Faturamento',
  settings:     '⚙️ Configurações',
  integrations: '🔗 Integrações',
  notifications:'🔔 Notificações',
  audit:        '🔍 Auditoria',
};

const PERM_LABELS = {
  VIEW_ALL_APPOINTMENTS:  'Ver todos os agendamentos',
  VIEW_OWN_APPOINTMENTS:  'Ver apenas os próprios',
  CREATE_APPOINTMENT:     'Criar agendamentos',
  EDIT_APPOINTMENT:       'Editar agendamentos',
  DELETE_APPOINTMENT:     'Excluir agendamentos',
  MANAGE_USERS:           'Gerenciar usuários e perfis',
  VIEW_BILLING:           'Ver faturamento',
  MANAGE_BILLING:         'Gerenciar cobranças',
  MANAGE_TENANT_SETTINGS: 'Configurações do sistema',
  CONNECT_GOOGLE_CALENDAR:'Conectar Google Agenda',
  VIEW_NOTIFICATIONS:     'Ver notificações',
  VIEW_AUDIT_LOGS:        'Ver logs de auditoria',
};

function groupByCategory(permissions) {
  const groups = {};
  permissions.forEach(p => {
    const cat = p.category || 'other';
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(p);
  });
  return groups;
}

export default function TenantRoles() {
  const confirm = useConfirm();
  const [roles,       setRoles]       = useState([]);
  const [allPerms,    setAllPerms]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [modal,       setModal]       = useState(null); // null | 'create' | role obj
  const [form,        setForm]        = useState({ name: '', permissions: [] });
  const [saving,      setSaving]      = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([getRoles(), getPermissions()])
      .then(([r, p]) => {
        setRoles(r.data.roles || []);
        setAllPerms(p.data.permissions || []);
      })
      .catch(() => toast.error('Erro ao carregar perfis'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setForm({ name: '', permissions: [] });
    setModal('create');
  };

  const openEdit = role => {
    setForm({ name: role.name, permissions: [...role.permissions] });
    setModal(role);
  };

  const togglePerm = perm => {
    setForm(f => ({
      ...f,
      permissions: f.permissions.includes(perm)
        ? f.permissions.filter(p => p !== perm)
        : [...f.permissions, perm],
    }));
  };

  const onSave = async e => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Nome do perfil é obrigatório'); return; }
    setSaving(true);
    try {
      if (modal === 'create') {
        await createRole(form);
        toast.success('Perfil criado com sucesso');
      } else {
        await updateRole(modal.id, form);
        toast.success('Perfil atualizado');
      }
      setModal(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao salvar');
    } finally { setSaving(false); }
  };

  const onDelete = async role => {
    const ok = await confirm({
      title: `Excluir perfil "${role.name}"?`,
      message: 'Esta ação não pode ser desfeita. Usuários que ainda tinham este perfil atribuído ficarão sem perfil definido até serem reatribuídos manualmente.',
      confirmText: 'Excluir perfil',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await deleteRole(role.id);
      toast.success('Perfil excluído');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao excluir');
    }
  };

  const grouped = groupByCategory(allPerms);

  return (
    <div className="page-body">
      <div className="page-header">
        <div>
          <h2>Perfis de Acesso</h2>
          <p>Crie perfis personalizados com os nomes do seu negócio e controle o que cada um pode fazer.</p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}>+ Novo Perfil</button>
      </div>

      {loading ? (
        <div className="loading-screen"><div className="spinner" /></div>
      ) : !roles.length ? (
        <div className="empty-state">
          <div className="empty-icon">🎭</div>
          <h3>Nenhum perfil criado</h3>
          <p>Crie perfis como "Artista", "Recepcionista", "Gerente" com as permissões certas.</p>
          <button className="btn btn-primary" onClick={openCreate}>Criar primeiro perfil</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {roles.map(role => (
            <div key={role.id} className="card">
              <div className="card-body" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <strong style={{ fontSize: 15, color: 'var(--gray-900)' }}>{role.name}</strong>
                    {role.is_system && (
                      <span className="badge badge-info">Sistema</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {role.permissions.length === 0 ? (
                      <span style={{ fontSize: 12, color: 'var(--gray-400)' }}>Sem permissões</span>
                    ) : role.permissions.map(p => (
                      <span key={p} className="chip" style={{ fontSize: 11 }}>
                        {PERM_LABELS[p] || p}
                      </span>
                    ))}
                  </div>
                </div>
                {!role.is_system && (
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => openEdit(role)}>Editar</button>
                    <button className="btn btn-danger btn-sm" onClick={() => onDelete(role)}>Excluir</button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{modal === 'create' ? 'Novo Perfil' : `Editar: ${modal.name}`}</h3>
              <button className="modal-close" onClick={() => setModal(null)}>×</button>
            </div>
            <form onSubmit={onSave}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Nome do perfil *</label>
                  <input
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder='Ex: Artista, Recepcionista, Gerente...'
                    required
                    disabled={modal?.is_system}
                  />
                  <small>Use o nome que faz sentido no seu negócio.</small>
                </div>

                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Permissões</label>
                  <p style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 12 }}>
                    Selecione o que este perfil pode fazer no sistema:
                  </p>

                  {Object.entries(grouped).map(([cat, perms]) => (
                    <div key={cat} style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--gray-600)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.5px' }}>
                        {CATEGORY_LABELS[cat] || cat}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {perms.map(p => (
                          <div key={p.name}
                            onClick={() => togglePerm(p.name)}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                              borderRadius: 8, border: '1.5px solid',
                              borderColor: form.permissions.includes(p.name) ? 'var(--primary)' : 'var(--gray-200)',
                              background: form.permissions.includes(p.name) ? 'rgba(37,99,235,0.06)' : 'transparent',
                              cursor: 'pointer', transition: 'all .15s',
                            }}>
                            <Toggle checked={form.permissions.includes(p.name)} onChange={() => {}} />
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--gray-800)' }}>
                                {PERM_LABELS[p.name] || p.name}
                              </div>
                              {p.description && (
                                <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 1 }}>{p.description}</div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setModal(null)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Salvando…' : 'Salvar Perfil'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
